/**
 * InsuranceQuoteCard
 * ------------------
 * Surfaces the seller's insurance application state at-a-glance and gives them
 * the Pay-in-Full vs Quarterly accept dialog when a quote is ready.
 *
 * Renders nothing if there's no application yet OR the policy is already active
 * (let the parent page render its own active-policy widgets).
 *
 * Drop-in usage:
 *   import InsuranceQuoteCard from "../../components/InsuranceQuoteCard";
 *   <InsuranceQuoteCard onPolicyActivated={refreshPolicy} />
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import StepUpModal from "./StepUpModal";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InsuranceQuoteCard({ onPolicyActivated }) {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [plan, setPlan] = useState("full");
  const [submitting, setSubmitting] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/seller/master-policy/status");
      setApp(data || null);
    } catch (e) {
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const premium = Number(app?.quote_premium_total || 0);
    const surcharge = Number(app?.installment_fee_pct ?? 0.10);
    const downPct = Number(app?.downpayment_pct ?? 0.25);
    const quarterlyTotal = +(premium * (1 + surcharge)).toFixed(2);
    const downpayment = +(quarterlyTotal * downPct).toFixed(2);
    const installment = +((quarterlyTotal - downpayment) / 3).toFixed(2);
    return { premium, quarterlyTotal, downpayment, installment };
  }, [app]);

  const accept = async (stepUpToken) => {
    setSubmitting(true);
    try {
      await api.post(
        `/seller/master-policy-applications/${app.id}/accept-quote`,
        { plan },
        stepUpToken ? { headers: { "X-Step-Up-Token": stepUpToken } } : undefined,
      );
      toast.success(plan === "full" ? "Premium paid in full — policy is active" : "Downpayment received — installments scheduled");
      setDialogOpen(false);
      setStepUpOpen(false);
      await load();
      onPolicyActivated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail?.code === "stepup_required") {
        setStepUpOpen(true);
        return;
      }
      toast.error(detail?.message || detail || "Couldn't process payment — check wallet balance");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !app) return null;
  const s = app.status;
  if (["not_started", "active", "approved"].includes(s)) return null;   // parent handles

  // ── DECLINED ─────────────────────────────────────────────────────
  if (s === "declined") {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="insurance-status-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-medium">Coverage application declined</div>
            <div className="text-[12.5px] text-foreground/65 mt-1">{app.decline_reason || "Reach out to support to discuss next steps."}</div>
          </div>
        </div>
      </div>
    );
  }

  // ── PENDING REVIEW / REAPPLY ────────────────────────────────────
  if (s === "pending_review" || s === "reapply_pending") {
    return (
      <div className="rounded-2xl border border-border bg-secondary/30 p-5" data-testid="insurance-status-card">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-foreground/60 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-medium">We're finalizing your coverage</div>
            <div className="text-[12.5px] text-foreground/65 mt-1">Typical decision in 2–3 business days. We'll email you the moment your quote is ready.</div>
          </div>
        </div>
      </div>
    );
  }

  // ── QUOTED — show accept dialog trigger ─────────────────────────
  if (s === "quoted") {
    return (
      <>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 space-y-4" data-testid="insurance-quote-card">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0"/>
            <div className="flex-1">
              <div className="font-medium">Your {app.quote_insurer || "Allianz Trade"} quote is ready</div>
              <div className="text-[12.5px] text-foreground/65 mt-1">
                Coverage limit: <span className="mono font-medium">{money(app.quote_coverage_limit)}</span> · Term: {app.quote_policy_term_months || 12} months
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-[13px]">
            <div className="rounded-xl bg-background border border-border p-3">
              <div className="text-foreground/55 text-[11px] uppercase tracking-wider">Annual premium</div>
              <div className="mono text-lg mt-1">{money(totals.premium)}</div>
            </div>
            <div className="rounded-xl bg-background border border-border p-3">
              <div className="text-foreground/55 text-[11px] uppercase tracking-wider">Quarterly total (incl. 10% fee)</div>
              <div className="mono text-lg mt-1">{money(totals.quarterlyTotal)}</div>
            </div>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="insurance-accept-quote-btn"
            className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
            Review &amp; pay from wallet
          </Button>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="insurance-accept-dialog">
            <DialogHeader>
              <DialogTitle className="display text-xl">Choose your payment plan</DialogTitle>
            </DialogHeader>
            <p className="text-[12.5px] text-foreground/65 -mt-1">Premium debits directly from your verqo wallet. Policy activates after payment.</p>
            <div className="space-y-2 mt-3">
              {[
                { v: "full",      l: "Pay in full",  fee: money(totals.premium),
                  hint: "Policy active immediately. No installment fee." },
                { v: "quarterly", l: "Pay quarterly", fee: `${money(totals.downpayment)} today`,
                  hint: `25% down + 3 × ${money(totals.installment)} every 90 days · 10% installment fee` },
              ].map(opt => (
                <button key={opt.v} type="button" onClick={() => setPlan(opt.v)}
                  data-testid={`insurance-plan-${opt.v}`}
                  className={`w-full text-left flex items-center gap-3 p-3.5 rounded-xl border transition-colors ${plan === opt.v ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium flex items-center justify-between gap-2">
                      <span>{opt.l}</span>
                      <span className="mono">{opt.fee}</span>
                    </div>
                    <div className="text-[11.5px] text-foreground/55 mt-0.5">{opt.hint}</div>
                  </div>
                  {plan === opt.v && <CheckCircle2 className="w-4 h-4 text-foreground shrink-0"/>}
                </button>
              ))}
            </div>
            <Button onClick={accept} disabled={submitting} data-testid="insurance-confirm-pay"
              className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Processing…</> : `Pay ${plan === "full" ? money(totals.premium) : money(totals.downpayment)}`}
            </Button>
            <p className="text-[10.5px] text-foreground/45 text-center mt-2">
              You'll get a policy number from our team within 24h after payment.
            </p>
          </DialogContent>
        </Dialog>
        <StepUpModal
          open={stepUpOpen}
          onClose={() => setStepUpOpen(false)}
          action="insurance_payment"
          amount={plan === "full" ? totals.premium : totals.downpayment}
          title={`Confirm ${plan === "full" ? "premium" : "downpayment"} payment`}
          description="Every wallet debit is protected by SMS verification."
          onVerified={(token) => accept(token)}
        />
      </>
    );
  }

  // ── AWAITING PAYMENT (quarterly · installment due) ───────────────
  if (s === "awaiting_payment") {
    const next = app.next_installment_due ? new Date(app.next_installment_due).toLocaleDateString() : "—";
    const arrears = app.arrears_days || 0;
    const variant = arrears > 0
      ? "border-amber-200 bg-amber-50/60"
      : "border-border bg-secondary/30";
    return (
      <div className={`rounded-2xl border ${variant} p-5`} data-testid="insurance-status-card">
        <div className="flex items-start gap-3">
          <Clock className={`w-5 h-5 mt-0.5 shrink-0 ${arrears > 0 ? "text-amber-700" : "text-foreground/60"}`}/>
          <div className="flex-1">
            <div className="font-medium">
              {arrears > 0 ? `${arrears} days overdue` : "Quarterly installments active"}
            </div>
            <div className="text-[12.5px] text-foreground/65 mt-1">
              {app.installments_remaining} installments remaining · Next debit: <span className="mono">{next}</span> · Outstanding: <span className="mono">{money(app.amount_outstanding)}</span>
            </div>
            {arrears >= 7 && (
              <div className="text-[12px] text-amber-800 mt-2">⚠ Policy will be cancelled at day 10 if not paid. Add funds to your wallet now.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── CANCELLED ────────────────────────────────────────────────────
  if (s === "cancelled") {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50/40 p-5" data-testid="insurance-status-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-700 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-medium">Policy cancelled — installment unpaid</div>
            <div className="text-[12.5px] text-foreground/65 mt-1">{app.cancellation_reason || "Reach out to support to restore coverage."}</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
